import React, { useEffect, useState } from "react";

/**
 * Danny's Game Room — Terms & Conditions Component
 *
 * Usage:
 * <TermsAndConditionsDGR
 *   lastUpdated="October, 2025"
 *   websiteUrl="https://www.dannysgameroom.com"
 *   contactEmail="support@dannysgameroom.com"
 *   governingLaw="Pakistan"
 *   arbitrationLocation="Karachi, Pakistan"
 * />
 */
export default function TermsAndConditionsDGR({
  lastUpdated = "October, 2025",
  websiteUrl = "https://www.dannysgameroom.com",
  contactEmail = "support@dannysgameroom.com",
  governingLaw = "[Your State/Country]",
  arbitrationLocation = "[Your Location]",
}) {
  const sections = [
    { id: "eligibility", title: "1. Eligibility" },
    { id: "account", title: "2. Account Registration" },
    { id: "use", title: "3. Use of the Services" },
    { id: "payments", title: "4. Deposits, Withdrawals, and Payments" },
    { id: "bonuses", title: "5. Bonuses and Promotions" },
    { id: "responsible", title: "6. Gambling and Responsible Gaming" },
    { id: "ip", title: "7. Intellectual Property" },
    { id: "ugc", title: "8. User-Generated Content" },
    { id: "termination", title: "9. Termination and Suspension of Accounts" },
    { id: "privacy", title: "10. Privacy and Data Protection" },
    { id: "liability", title: "11. Limitation of Liability" },
    { id: "indemnification", title: "12. Indemnification" },
    { id: "law", title: "13. Governing Law and Dispute Resolution" },
    { id: "changes", title: "14. Changes to Terms and Conditions" },
    { id: "contact", title: "15. Contact Us" },
  ];

  // Active section highlight
  const [activeId, setActiveId] = useState("eligibility");
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      {
        // Trigger as the section crosses ~25% from top
        root: null,
        rootMargin: "0px 0px -75% 0px",
        threshold: 0.1,
      }
    );

    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen w-full bg-white text-gray-900">
      {/* Local style override for lists if global CSS disables bullets */}
      <style>{`
        .tos-list { list-style: disc !important; padding-left: 1.25rem; }
        .tos-num  { list-style: decimal !important; padding-left: 1.25rem; }
      `}</style>

      <div className="mx-auto max-w-6xl px-4 py-8 md:py-12">
        {/* Header */}
        <header className="mb-8 rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-50 to-white p-6 shadow-sm">
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Terms and Conditions</h1>
          <p className="mt-2 text-sm text-gray-600">
            Last Updated: <span className="font-medium">{lastUpdated}</span>
          </p>
          <p className="mt-1 text-sm text-gray-600">
            Welcome to Danny's Game Room ("we", "our", "us"). By accessing or using our website, {websiteUrl}, and engaging with our gambling apps and games (collectively, the "Services"), you agree to comply with and be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, please do not use our Website or Services.
          </p>
        </header>

        {/* Layout: Sidebar TOC + Content */}
        <div className="grid gap-8 md:grid-cols-[260px,1fr]">
          {/* TOC */}
          <nav className="md:sticky md:top-6 h-fit rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-700">On this page</h2>
            <ul className="space-y-2 text-sm">
              {sections.map((s) => {
                const isActive = activeId === s.id;
                return (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        const el = document.getElementById(s.id);
                        el?.scrollIntoView({ behavior: "smooth", block: "start" });
                        setActiveId(s.id);
                      }}
                      aria-current={isActive ? "location" : undefined}
                      className={[
                        "block rounded-md px-2 py-1 focus:outline-none focus:ring-2",
                        isActive
                          ? "bg-indigo-50 text-indigo-700 ring-indigo-200 border-l-2 border-indigo-500"
                          : "hover:bg-gray-50 hover:text-gray-900 text-gray-700",
                      ].join(" ")}
                    >
                      {s.title}
                    </a>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Main Content */}
          <main className="space-y-8">
            {/* 1. Eligibility */}
            <section id="eligibility" className="scroll-mt-24 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold">1. Eligibility</h3>
              <p className="mt-3 text-gray-700">By using our Services, you affirm that you:</p>
              <ul className="tos-list mt-2 text-gray-700">
                <li>Are at least 18 years old (or the legal age for gambling in your jurisdiction, whichever is higher).</li>
                <li>Have the legal capacity to enter into and comply with these Terms.</li>
                <li>Are not located in a jurisdiction where online gambling is prohibited.</li>
              </ul>
              <p className="mt-3 text-gray-700">We reserve the right to verify your eligibility at any time, and if you do not meet the eligibility criteria, we may suspend or terminate your account.</p>
            </section>

            {/* 2. Account Registration */}
            <section id="account" className="scroll-mt-24 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold">2. Account Registration</h3>
              <p className="mt-3 text-gray-700">
                To access certain features of the Website and play games, you must create an account. When registering, you agree to provide accurate, current, and complete information and to maintain and promptly update your account information to ensure its accuracy.
              </p>
              <p className="mt-3 text-gray-700">
                You are responsible for maintaining the confidentiality of your account credentials (username and password) and for all activities under your account. You must immediately notify us if you suspect any unauthorized access to or use of your account.
              </p>
            </section>

            {/* 3. Use of the Services */}
            <section id="use" className="scroll-mt-24 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold">3. Use of the Services</h3>
              <p className="mt-3 text-gray-700">You agree to use our Services in accordance with all applicable laws and regulations. You shall not:</p>
              <ul className="tos-list mt-2 text-gray-700">
                <li>Engage in any form of cheating, fraud, or manipulation of the games.</li>
                <li>Use the Services for any unlawful, harmful, or malicious purposes.</li>
                <li>Impersonate any other individual or entity, or misrepresent your affiliation with any entity.</li>
                <li>Engage in any activities that disrupt, overload, or damage the Website or Services.</li>
              </ul>
            </section>

            {/* 4. Payments */}
            <section id="payments" className="scroll-mt-24 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold">4. Deposits, Withdrawals, and Payments</h3>
              <p className="mt-3 text-gray-700">
                You agree to comply with our payment terms for deposits, withdrawals, and any associated fees. All deposits made to your account must come from legitimate sources, and all withdrawals will be processed in accordance with our withdrawal policy.
              </p>
              <p className="mt-3 text-gray-700">
                You are solely responsible for ensuring that the payment methods you use are valid, authorized, and compliant with relevant laws. We reserve the right to suspend or close accounts that are involved in fraudulent activities, such as chargebacks or misuse of payment methods.
              </p>
            </section>

            {/* 5. Bonuses and Promotions */}
            <section id="bonuses" className="scroll-mt-24 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold">5. Bonuses and Promotions</h3>
              <p className="mt-3 text-gray-700">
                From time to time, we may offer bonuses, promotions, or rewards to users. These offers will be subject to specific terms, which will be outlined at the time of the promotion. You agree to abide by all rules, restrictions, and eligibility requirements for each promotion.
              </p>
              <p className="mt-3 text-gray-700">We reserve the right to modify or cancel any bonuses or promotions at our discretion.</p>
            </section>

            {/* 6. Responsible Gaming */}
            <section id="responsible" className="scroll-mt-24 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold">6. Gambling and Responsible Gaming</h3>
              <p className="mt-3 text-gray-700">
                We promote responsible gambling and encourage users to play within their means. If you believe you or someone you know may have a gambling problem, we recommend seeking help immediately.
              </p>
              <p className="mt-3 text-gray-700">
                We offer self-exclusion and account limitation features. If you wish to limit your gaming activity, you can contact us at <a href={`mailto:${contactEmail}`} className="underline decoration-gray-300 hover:decoration-gray-600">{contactEmail}</a> for assistance.
              </p>
              <p className="mt-3 text-gray-700">
                We also reserve the right to suspend or close accounts of users engaging in problematic gambling behavior or violating responsible gaming policies.
              </p>
            </section>

            {/* 7. Intellectual Property */}
            <section id="ip" className="scroll-mt-24 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold">7. Intellectual Property</h3>
              <p className="mt-3 text-gray-700">
                All content on the Website, including but not limited to software, logos, graphics, text, and images, is the property of Danny's Game Room or its licensors and is protected by intellectual property laws. You may not copy, reproduce, modify, or distribute any content without our explicit permission.
              </p>
            </section>

            {/* 8. User-Generated Content */}
            <section id="ugc" className="scroll-mt-24 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold">8. User-Generated Content</h3>
              <p className="mt-3 text-gray-700">If the Website allows users to post content (e.g., reviews, comments, forum posts), you agree that such content will be non-confidential, non-proprietary, and may be publicly accessible. You agree not to post content that:</p>
              <ul className="tos-list mt-2 text-gray-700">
                <li>Is illegal, harmful, or defamatory.</li>
                <li>Violates any third-party rights.</li>
                <li>Is obscene, offensive, or inappropriate.</li>
              </ul>
              <p className="mt-3 text-gray-700">We reserve the right to remove or edit user-generated content that violates these Terms.</p>
            </section>

            {/* 9. Termination */}
            <section id="termination" className="scroll-mt-24 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold">9. Termination and Suspension of Accounts</h3>
              <p className="mt-3 text-gray-700">We reserve the right to suspend or terminate your account at our sole discretion, without prior notice, for any reason, including but not limited to:</p>
              <ul className="tos-list mt-2 text-gray-700">
                <li>Violation of these Terms.</li>
                <li>Engaging in fraudulent or illegal activities.</li>
                <li>Inactivity on your account for a prolonged period.</li>
                <li>Suspected underage gambling.</li>
              </ul>
              <p className="mt-3 text-gray-700">If your account is terminated, you may lose access to any funds in your account, subject to applicable withdrawal policies.</p>
            </section>

            {/* 10. Privacy */}
            <section id="privacy" className="scroll-mt-24 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold">10. Privacy and Data Protection</h3>
              <p className="mt-3 text-gray-700">
                Your use of our Services is also governed by our Privacy Policy, which outlines how we collect, use, and protect your personal information. By using our Website and Services, you agree to our Privacy Policy.
              </p>
            </section>

            {/* 11. Limitation of Liability */}
            <section id="liability" className="scroll-mt-24 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold">11. Limitation of Liability</h3>
              <p className="mt-3 text-gray-700">To the fullest extent permitted by law, Danny's Game Room, its affiliates, officers, directors, and employees are not liable for any direct, indirect, incidental, special, or consequential damages arising out of your use of the Website or Services. This includes, but is not limited to, losses resulting from:</p>
              <ul className="tos-list mt-2 text-gray-700">
                <li>Gambling losses.</li>
                <li>System or service interruptions.</li>
                <li>Errors in software or data transmission.</li>
                <li>Loss of account access or data.</li>
              </ul>
            </section>

            {/* 12. Indemnification */}
            <section id="indemnification" className="scroll-mt-24 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold">12. Indemnification</h3>
              <p className="mt-3 text-gray-700">
                You agree to indemnify, defend, and hold harmless Danny's Game Room, its affiliates, and employees from any claims, damages, liabilities, and expenses (including legal fees) arising out of or related to your violation of these Terms, your use of the Website, or your engagement in any unlawful activities.
              </p>
            </section>

            {/* 13. Governing Law */}
            <section id="law" className="scroll-mt-24 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold">13. Governing Law and Dispute Resolution</h3>
              <p className="mt-3 text-gray-700">
                These Terms shall be governed by and construed in accordance with the laws of {governingLaw}. Any dispute arising out of or in connection with these Terms shall be resolved through binding arbitration in {arbitrationLocation}, unless otherwise required by applicable law.
              </p>
            </section>

            {/* 14. Changes */}
            <section id="changes" className="scroll-mt-24 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold">14. Changes to Terms and Conditions</h3>
              <p className="mt-3 text-gray-700">
                We reserve the right to modify, amend, or update these Terms at any time. Any changes will be posted on this page with an updated "Last Updated" date. Your continued use of the Website after any changes indicates your acceptance of the revised Terms.
              </p>
            </section>

            {/* 15. Contact */}
            <section id="contact" className="scroll-mt-24 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold">15. Contact Us</h3>
              <p className="mt-3 text-gray-700">If you have any questions or concerns regarding these Terms and Conditions, please contact us at:</p>
              <div className="mt-2 rounded-xl border border-gray-200 bg-gray-50 p-4">
                <p className="font-medium">Danny's Chat Support</p>
                <p className="text-gray-700">
                  Email: <a href={`mailto:${contactEmail}`} className="underline text-black decoration-gray-300 hover:decoration-gray-600">{contactEmail}</a>
                </p>
              </div>
            </section>

            {/* Note */}
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
              <p className="text-sm">
                <strong>Note:</strong> This component provides general Terms content. It is not legal advice. Please consult an attorney to ensure compliance with all applicable laws and regulations in your jurisdiction.
              </p>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
